import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, TrendingUpIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
// Fixed sample daily usage data (realistic household pattern)
const DAILY_USAGE = [{
  day: '1',
  kwh: 8.2
}, {
  day: '2',
  kwh: 9.5
}, {
  day: '3',
  kwh: 12.1
}, {
  day: '4',
  kwh: 7.8
}, {
  day: '5',
  kwh: 8.4
}, {
  day: '6',
  kwh: 14.3
}, {
  day: '7',
  kwh: 15.7
}, {
  day: '8',
  kwh: 9.1
}, {
  day: '9',
  kwh: 8.6
}, {
  day: '10',
  kwh: 7.9
}, {
  day: '11',
  kwh: 9.2
}, {
  day: '12',
  kwh: 8.8
}, {
  day: '13',
  kwh: 13.5
}, {
  day: '14',
  kwh: 16.2
}, {
  day: '15',
  kwh: 10.4
}, {
  day: '16',
  kwh: 9.7
}, {
  day: '17',
  kwh: 8.3
}, {
  day: '18',
  kwh: 9.0
}, {
  day: '19',
  kwh: 8.1
}, {
  day: '20',
  kwh: 12.8
}, {
  day: '21',
  kwh: 14.9
}, {
  day: '22',
  kwh: 10.2
}, {
  day: '23',
  kwh: 9.4
}, {
  day: '24',
  kwh: 8.7
}, {
  day: '25',
  kwh: 9.6
}, {
  day: '26',
  kwh: 8.5
}, {
  day: '27',
  kwh: 13.1
}, {
  day: '28',
  kwh: 15.4
}, {
  day: '29',
  kwh: 10.8
}, {
  day: '30',
  kwh: 9.3
}];
const today = new Date();
const currentDay = today.getDate();
export function MonthlyEnergyWidget() {
  const currentUsage = 287;
  const monthlyBill = 45200;
  const lastMonthUsage = 312;
  const percentChange = ((currentUsage - lastMonthUsage) / lastMonthUsage * 100).toFixed(1);
  const isDown = currentUsage < lastMonthUsage;
  return <div className="glass-panel rounded-2xl p-4 flex flex-col gap-3 flex-1 h-full" data-id="element-136">
      <div className="flex items-center space-x-2 text-slate-400" data-id="element-137">
        <CalendarIcon className="w-4 h-4 text-cyan-400" data-id="element-138" />
        <span className="text-[11px] font-medium uppercase tracking-wider" style={{
        fontSize: '12px'
      }} data-id="element-139">
          이달의 전기 사용
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3" data-id="element-140">
        {/* Cumulative Usage */}
        <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5" data-id="element-141">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1" data-id="element-142">
            누적 사용량
          </div>
          <div className="flex items-baseline gap-1" data-id="element-143">
            <span className="text-xl font-mono font-bold text-white" data-id="element-144">
              {currentUsage}
            </span>
            <span className="text-[10px] text-slate-500 font-mono" data-id="element-145">kWh</span>
          </div>
          <div className={`flex items-center gap-1 mt-1.5 text-[9px] font-mono ${isDown ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-146">
            <TrendingUpIcon className={`w-3 h-3 ${isDown ? 'rotate-180' : ''}`} data-id="element-147" />
            <span data-id="element-148">{Math.abs(Number(percentChange))}% vs 지난달</span>
          </div>
        </div>

        {/* Monthly Bill */}
        <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5" data-id="element-149">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1" data-id="element-150">
            예상 전기료
          </div>
          <div className="flex items-baseline gap-0.5" data-id="element-151">
            <span className="text-[10px] text-slate-400 font-mono" data-id="element-152">₩</span>
            <span className="text-xl font-mono font-bold text-amber-400 glow-text-amber" data-id="element-153">
              {monthlyBill.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-slate-800 h-1 mt-2 rounded-full overflow-hidden" data-id="element-154">
            <div className="bg-gradient-to-r from-amber-500 to-amber-400 h-full rounded-full opacity-80" style={{
            width: `${currentUsage / 400 * 100}%`
          }} data-id="element-155" />
          </div>
          <div className="text-[8px] text-slate-600 text-right mt-1 font-mono" data-id="element-156">
            목표 400kWh
          </div>
        </div>
      </div>

      {/* Daily Usage Chart */}
      <div className="flex-1 min-h-0 flex flex-col" data-id="element-157">
        <div className="flex items-center justify-between mb-1.5" data-id="element-158">
          <span className="text-[9px] text-slate-500 uppercase tracking-wider font-bold" data-id="element-159">
            일별 사용량
          </span>
          <span className="text-[9px] text-slate-600 font-mono" data-id="element-160">
            1일 ~ {currentDay}일
          </span>
        </div>
        <div className="flex-1 min-h-[60px]" data-id="element-161">
          <ResponsiveContainer width="100%" height="100%" data-id="element-162">
            <BarChart data={DAILY_USAGE} barCategoryGap="15%" data-id="element-163">
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
              fill: '#475569',
              fontSize: 7,
              fontFamily: 'monospace'
            }} interval={Math.max(0, Math.floor(currentDay / 7) - 1)} data-id="element-164" />
              <Tooltip cursor={{
              fill: 'rgba(255,255,255,0.03)'
            }} contentStyle={{
              backgroundColor: '#0a0f1a',
              borderColor: 'rgba(255,255,255,0.1)',
              borderRadius: '8px',
              fontSize: '10px',
              fontFamily: 'monospace'
            }} itemStyle={{
              color: '#22d3ee'
            }} labelFormatter={label => `${label}일`} formatter={(value: number) => [`${value} kWh`, '사용량']} data-id="element-165" />
              <Bar dataKey="kwh" radius={[2, 2, 0, 0]} fill="#22d3ee" opacity={0.7} data-id="element-166" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}